import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Compacte widget (18px padding) in plaats van een contentkaart (24px). */
  compact?: boolean;
  /** Prominent paneel: radius-xl in plaats van radius-lg. */
  hero?: boolean;
}

/**
 * Een kaart is een **afzonderlijk object**: een bericht, een melding, een tegel.
 *
 * Niet een sectie, en niet een pagina. Dat onderscheid raakte hier zoek, en het resultaat was een
 * beginscherm waarop elk blok dezelfde witte kaart was met dezelfde radius en dezelfde schaduw —
 * de pushvraag, de snelkoppelingen, de formulieren, de tijdlijn en zelfs de instellingen. Als
 * alles even zwaar is, valt niets op, en dan moet je elk blok lezen om te weten wat het is. Een
 * sectie krijgt sinds UI-3 een `SectionHead` op de achtergrond; een kaart is wat erónder staat.
 *
 * **Twee schaduwniveaus, en ze betekenen iets.** `shadow-sm` is wat op de pagina ligt; `shadow-lg`
 * is wat erboven zweeft (dialoog, toast). Er zat hier een `hover:shadow-md`: elke kaart kwam
 * omhoog zodra de muis erover ging, ook een kaart waar niets te klikken viel. Daarmee betekende
 * hoogte "de muis is hier" in plaats van "dit ligt bovenop", en op een telefoon — waar geen muis
 * is — betekende het niets. Een kaart die wél ergens heen gaat, laat dat zien met haar rand
 * (`hover:border-brand-primary`), en dat werkt ook onder een vinger.
 *
 * **Radius naar rol:** een pagina-vullend paneel `xl`, een gewone kaart `lg`, een compacte kaart
 * `md`. Kleiner object, kleinere ronding — anders lijkt een tegel van 80 px even "zacht" als een
 * paneel van 600.
 */
export function Card({ className, compact, hero, ...props }: CardProps) {
  return (
    <div
      className={cn(
        'border border-border bg-surface shadow-sm',
        hero ? 'rounded-xl' : compact ? 'rounded-md' : 'rounded-lg',
        compact ? 'p-card-compact' : 'p-card',
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('mb-element flex items-center justify-between', className)} {...props} />
  );
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2
      className={cn('font-heading text-card-title font-semibold text-text-primary', className)}
      {...props}
    />
  );
}

export interface EmptyStateProps {
  title: string;
  description?: string;
  action?: ReactNode;
  /**
   * Een ander pictogram dan de sparkle.
   *
   * De sparkle is het brandanker voor "er is hier niets, en dat is goed nieuws". Soms is het dat
   * niet: een scherm dat offline is, hoort een wifi-icoon te dragen (Stap 17, spec §4). Zonder
   * deze uitweg zou elk zo'n geval een eigen kaart worden, en dan loopt de vorm uit elkaar.
   */
  icon?: ReactNode;
}

/** Lege toestand met het sparkle-brandanker (style guide §Sparkle als brand-element). */
export function EmptyState({ title, description, action, icon }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center gap-element px-card py-10 text-center">
      <span
        aria-hidden
        className="flex h-12 w-12 items-center justify-center rounded-pill bg-brand-primary-tint text-brand-primary-dark"
      >
        {icon ?? <Sparkle className="h-6 w-6" />}
      </span>
      <p className="font-heading text-section font-semibold text-text-primary">{title}</p>
      {description ? (
        <p className="max-w-prose text-copy text-text-secondary">{description}</p>
      ) : null}
      {action}
    </div>
  );
}

/** Het vierpuntige UP2D8-sparkle uit het brandbook. Max 1–2 tegelijk zichtbaar. */
export function Sparkle({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={className} focusable="false">
      <path
        d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Laadtoestand: skeletregels in plaats van een spinner, zodat de lay-out niet springt. */
/**
 * Wachten, in de vorm van wat er komt.
 *
 * De eerste versie toonde drie streepjes van 4 px op een lege achtergrond. Dat zegt "er gebeurt
 * iets", maar niet wát — en op het moment dat de inhoud binnenkomt, springt het scherm van drie
 * dunne lijnen naar drie blokken van 160 px. Nu heeft elke plaatshouder de vórm van wat hem
 * vervangt: een titelregel en twee tekstregels, met ruimte ertussen.
 *
 * **Zonder eigen omlijsting**, en dat is geen vergetelheid. Deze component staat op 88 plaatsen,
 * en op lang niet alle staat hij los: in het beheer zit hij vaak al ín een kaart. Zou hij zijn
 * eigen rand en schaduw meebrengen, dan stond er een kaart in een kaart — precies de stapeling
 * die UI-3 wegneemt. De omlijsting komt van wat eromheen staat; de vorm komt van hier.
 *
 * `aria-live="polite"` met een verborgen zin erbij: een schermlezer hoort "Bezig met laden", geen
 * reeks betekenisloze blokken.
 */
export function LoadingState({ rows = 3, label }: { rows?: number; label?: string }) {
  return (
    <div role="status" aria-live="polite" className="flex flex-col gap-element">
      <span className="sr-only">{label ?? 'Bezig met laden…'}</span>
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} aria-hidden className="flex flex-col gap-2 py-1">
          <span className="h-4 w-2/3 animate-pulse rounded-pill bg-border-light" />
          <span className="h-3 w-full animate-pulse rounded-pill bg-border-light" />
          <span className="h-3 w-4/5 animate-pulse rounded-pill bg-border-light" />
        </div>
      ))}
    </div>
  );
}
